/**
 * NsectionB10Page
 * B.10.基準情景（A6.4-FORM-METH-002 Section 10. Baseline scenario）
 * 五主頁籤：10.1(Checkbox) / 10.2(子頁籤:10.2.1富文本+10.2.2公式) / 10.3 / 10.4(子頁籤:10.4富文本+10.4.1公式) / 10.5
 * 10.2.2 和 10.4.1 使用 FormulaWizard 三階段精靈（公式→參數→保守估算）
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import 'katex/dist/katex.min.css';
import { BlockMath, InlineMath } from 'react-katex';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import nsectionB10Service, { NsectionB10Detail, FormulaEntry, FormulaParameter } from '../services/nsectionB10Service';
import { getSystemCodes, SystemCode } from '../services/systemCodeService';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import LatexHelper from '../components/LatexHelper';
import { useToast } from '../contexts/ToastContext';
import { evaluateLatex as evalLatex, parseLatexSymbols } from '../utils/latexEvaluator';
import formulaConfigService, { FormulaConfigItem } from '../services/formulaConfigService';
import '../styles/DataTable.css';

// ── 常數 ──────────────────────────────────────────────────────────
type WizardStage = 'equations' | 'parameters' | 'conservative';
type FormulaFieldKey = 'baseline_calculate' | 'bau_calculate';

const EMPTY_ENTRY = (): FormulaEntry => ({
  id: crypto.randomUUID(),
  equation_note: '',
  equation: { latex: '' },
  parameters: [],
  calculation_result: { value: null, unit: '' },
});

function fmtNum(value: number | null | undefined, decimals = 5): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: decimals });
}

// evaluateLatex 已移至 src/utils/latexEvaluator.ts，此處透過 evalLatex 包裝使用
function evaluateLatex(latex: string, params: FormulaParameter[]): number | null {
  return evalLatex(latex, params.map(p => ({ symbol: p.symbol, trial_value: p.annual_value })));
}

const COMMON_UNITS = [
  { label: 'tCO₂e',  latex: '\\text{tCO}_2\\text{e}' },
  { label: 'MWh',    latex: '\\text{MWh}' },
  { label: 'GWh',    latex: '\\text{GWh}' },
  { label: 'kWh',    latex: '\\text{kWh}' },
  { label: 'GJ',     latex: '\\text{GJ}' },
  { label: 'MJ',     latex: '\\text{MJ}' },
  { label: 'kg',     latex: '\\text{kg}' },
  { label: 't',      latex: '\\text{t}' },
  { label: 'm³',     latex: '\\text{m}^3' },
  { label: '%',      latex: '\\%' },
  { label: 'tCO₂e/MWh', latex: '\\text{tCO}_2\\text{e/MWh}' },
  { label: 'tCO₂e/GJ',  latex: '\\text{tCO}_2\\text{e/GJ}' },
];

const APPROACH_OPTIONS = [
  { value: '36_i', labelZh: '最佳可得技術(BAT)，代表在經濟上可行且環境上合理的行動方案', labelEn: 'Best available technologies that represent an economically feasible and environmentally sound course of action, where appropriate.' },
  { value: '36_ii', labelZh: '雄心基準法，基線至少設定為同類活動在類似社會、經濟、環境及技術情況下，提供類似產出與服務之最佳績效活動的平均排放水平', labelEn: 'An ambitious benchmark approach where the baseline is set at least at the average emission level of the best performing comparable activities providing similar outputs and services in a defined scope in similar social, economic, environmental and technological circumstances.' },
  { value: '36_iii', labelZh: '基於現有實際或歷史排放量向下調整，確保符合 RMP 第 33 段', labelEn: 'An approach based on existing actual or historical emissions, adjusted downwards to ensure alignment with paragraph 33 of the RMP.' },
];

// ── LaTeX 參數解析 ────────────────────────────────────────────────

// ── 單位 LaTeX 輸入元件（複用 PDD B.7.1 UnitInput） ─────────────
interface UnitInputProps { value: string; onChange: (v: string) => void; disabled?: boolean; }
const UnitInput: React.FC<UnitInputProps> = ({ value, onChange, disabled = false }) => {
  const [showPicker, setShowPicker] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          placeholder="LaTeX unit" style={{ flex: 1, padding: '4px 6px', fontFamily: 'monospace', fontSize: 12, border: '1px solid #d0d0d0', borderRadius: 4 }} />
        {!disabled && (
          <button type="button" onClick={() => setShowPicker(p => !p)} title="常用單位"
            style={{ padding: '4px 7px', fontSize: 13, borderRadius: 5, border: '1px solid #d0d0d0', background: showPicker ? '#4A90E2' : '#fafafa', color: showPicker ? '#fff' : '#555', cursor: 'pointer', lineHeight: 1 }}>≡</button>
        )}
      </div>
      {value.trim() && (
        <div style={{ marginTop: 4, padding: '2px 6px', background: '#f8faff', border: '1px solid #d0e4ff', borderRadius: 5, fontSize: 13, display: 'inline-block' }}>
          {(() => { try { return <InlineMath math={value} />; } catch { return <span style={{ color: '#c33', fontSize: 11 }}>!</span>; } })()}
        </div>
      )}
      {showPicker && (
        <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 200, marginTop: 4, background: '#fff', border: '1px solid #d0d0d0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '8px 10px', width: 320 }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 6, fontWeight: 600 }}>常用單位（點擊插入）</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {COMMON_UNITS.map((u, i) => (
              <button key={i} type="button" onClick={() => { onChange(u.latex); setShowPicker(false); }}
                style={{ padding: '4px 10px', fontSize: 12, borderRadius: 5, border: '1px solid #e0e0e0', background: '#fafafa', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ color: '#888', fontSize: 11 }}>{u.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── 主元件 ────────────────────────────────────────────────────────
const NsectionB10Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProposalId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  const [data, setData] = useState<NsectionB10Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [mainTab, setMainTab] = useState('10.1');
  const [subTab102, setSubTab102] = useState('10.2.1');
  const [subTab104, setSubTab104] = useState('10.4');

  // Form state
  const [baselineApproaches, setBaselineApproaches] = useState<string[]>([]);
  const [approachJustification, setApproachJustification] = useState('');
  const [baselineIdentification, setBaselineIdentification] = useState('');
  const [baselineCalculate, setBaselineCalculate] = useState<FormulaEntry[]>([]);
  const [downwardAdjustedBaseline, setDownwardAdjustedBaseline] = useState('');
  const [bauScenarioIdentification, setBauScenarioIdentification] = useState('');
  const [bauCalculate, setBauCalculate] = useState<FormulaEntry[]>([]);
  const [baselineBauComparison, setBaselineBauComparison] = useState('');

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardField, setWizardField] = useState<FormulaFieldKey>('baseline_calculate');
  const [wizardEditEntry, setWizardEditEntry] = useState<FormulaEntry | undefined>(undefined);
  const [wizardStage, setWizardStage] = useState<WizardStage>('equations');
  const [wizardEntry, setWizardEntry] = useState<FormulaEntry>(EMPTY_ENTRY());
  const [paraTypeCodes, setParaTypeCodes] = useState<SystemCode[]>([]);
  const [wizardFormulaConfigs, setWizardFormulaConfigs] = useState<FormulaConfigItem[]>([]);
  const [wizardFormulaId, setWizardFormulaId] = useState<number | ''>('');

  const mainTabs = [
    { key: '10.1', label: isZh ? '10.1 基線方法選擇' : '10.1 Baseline approaches' },
    { key: '10.2', label: isZh ? '10.2 所選方法的應用' : '10.2 Application of approach' },
    { key: '10.3', label: isZh ? '10.3 向下調整後基線' : '10.3 Downward adjusted baseline' },
    { key: '10.4', label: isZh ? '10.4 保守 BAU 情境' : '10.4 Conservative BAU' },
    { key: '10.5', label: isZh ? '10.5 基線與 BAU 比較' : '10.5 Baseline vs BAU' },
  ];

  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('NsectionB10Page');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  useEffect(() => {
    getSystemCodes({ code_etype: 'equation parameters type', is_active: true })
      .then(codes => setParaTypeCodes(codes)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!wizardOpen) { setWizardFormulaId(''); return; }
    formulaConfigService.listActive().then(setWizardFormulaConfigs).catch(() => {});
  }, [wizardOpen]);

  useEffect(() => {
    if (!selectedProposalId) {
      setData(null); setBaselineApproaches([]); setApproachJustification('');
      setBaselineIdentification(''); setBaselineCalculate([]); setDownwardAdjustedBaseline('');
      setBauScenarioIdentification(''); setBauCalculate([]); setBaselineBauComparison('');
      return;
    }
    const load = async () => {
      try {
        setLoading(true); setError(null);
        const r = await nsectionB10Service.getByProposal(selectedProposalId);
        if (r) {
          setData(r);
          setBaselineApproaches(r.baseline_approaches || []);
          setApproachJustification(r.approach_justification || '');
          setBaselineIdentification(r.baseline_identification || '');
          setBaselineCalculate(r.baseline_calculate || []);
          setDownwardAdjustedBaseline(r.downward_adjusted_baseline || '');
          setBauScenarioIdentification(r.bau_scenario_identification || '');
          setBauCalculate(r.bau_calculate || []);
          setBaselineBauComparison(r.baseline_bau_comparison || '');
        } else {
          setData(null); setBaselineApproaches([]); setApproachJustification('');
          setBaselineIdentification(''); setBaselineCalculate([]); setDownwardAdjustedBaseline('');
          setBauScenarioIdentification(''); setBauCalculate([]); setBaselineBauComparison('');
        }
      } catch (err: any) {
        if (err.response?.status !== 404) setError(err.response?.data?.detail || (isZh ? '載入資料失敗' : 'Failed to load data'));
      } finally { setLoading(false); }
    };
    load();
  }, [selectedProposalId, isZh]);

  // ── 儲存 ───────────────────────────────────────────────────────
  const getSaveData = (): any => {
    if (mainTab === '10.1') return { baseline_approaches: baselineApproaches, approach_justification: approachJustification };
    if (mainTab === '10.2') {
      if (subTab102 === '10.2.1') return { baseline_identification: baselineIdentification };
      return { baseline_calculate: baselineCalculate };
    }
    if (mainTab === '10.3') return { downward_adjusted_baseline: downwardAdjustedBaseline };
    if (mainTab === '10.4') {
      if (subTab104 === '10.4') return { bau_scenario_identification: bauScenarioIdentification };
      return { bau_calculate: bauCalculate };
    }
    return { baseline_bau_comparison: baselineBauComparison };
  };

  const handleSave = async () => {
    if (!selectedProposalId) return;
    try {
      setLoading(true);
      const r = await nsectionB10Service.update(selectedProposalId, getSaveData());
      setData(r);
      toast.success(isZh ? '儲存成功' : 'Saved successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.detail || (isZh ? '儲存失敗' : 'Save failed'));
    } finally { setLoading(false); }
  };

  // ── FormulaWizard helpers ──────────────────────────────────────
  const getFormulas = (field: FormulaFieldKey) => field === 'baseline_calculate' ? baselineCalculate : bauCalculate;
  const setFormulas = (field: FormulaFieldKey, data: FormulaEntry[]) => {
    if (field === 'baseline_calculate') setBaselineCalculate(data);
    else setBauCalculate(data);
  };

  const openWizard = (field: FormulaFieldKey, entry?: FormulaEntry) => {
    setWizardField(field);
    setWizardEditEntry(entry);
    setWizardEntry(entry ? { ...entry, parameters: entry.parameters.map(p => ({ ...p })) } : EMPTY_ENTRY());
    setWizardStage('equations');
    setWizardOpen(true);
  };

  const updateParam = (idx: number, patch: Partial<FormulaParameter>) => {
    setWizardEntry(prev => {
      if (idx < 0 || idx >= prev.parameters.length) return prev;
      const params = [...prev.parameters];
      params[idx] = { ...params[idx], ...patch };
      return { ...prev, parameters: params };
    });
  };

  const removeParam = (idx: number) => {
    setWizardEntry(prev => ({
      ...prev,
      parameters: prev.parameters.filter((_, i) => i !== idx),
    }));
  };

  const handleWizardSave = () => {
    const formulas = [...getFormulas(wizardField)];
    if (wizardEditEntry) {
      const idx = formulas.findIndex(f => f.id === wizardEditEntry.id);
      if (idx >= 0) formulas[idx] = wizardEntry;
    } else {
      formulas.push(wizardEntry);
    }
    setFormulas(wizardField, formulas);
    setWizardOpen(false);
  };

  const deleteFormula = async (field: FormulaFieldKey, id: string) => {
    const ok = await toast.confirm({
      title: isZh ? '刪除確認' : 'Delete Confirmation',
      message: isZh ? '確定要刪除此公式嗎？' : 'Delete this formula?',
      confirmText: isZh ? '刪除' : 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    setFormulas(field, getFormulas(field).filter(f => f.id !== id));
  };

  // ── 權限 ───────────────────────────────────────────────────────
  if (permissionLoading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  if (!hasPermission('nsection_b10', 'read')) return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;
  const canEdit = hasPermission('nsection_b10', 'update');

  // ── 樣式 ───────────────────────────────────────────────────────
  const cardStyle: React.CSSProperties = { width: '100%', background: '#fff', borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8', padding: '28px 32px' };
  const tabBtn = (active: boolean): React.CSSProperties => ({ padding: '10px 16px', border: 'none', borderBottom: active ? '3px solid #4A90E2' : '3px solid transparent', background: active ? '#f0f7ff' : 'transparent', color: active ? '#4A90E2' : '#666', fontWeight: active ? 600 : 400, cursor: 'pointer', fontSize: 13, marginBottom: -2 });
  const subTabBtn = (active: boolean): React.CSSProperties => ({ padding: '8px 14px', border: 'none', borderBottom: active ? '2px solid #52c41a' : '2px solid transparent', background: active ? '#f6ffed' : 'transparent', color: active ? '#52c41a' : '#888', fontWeight: active ? 600 : 400, cursor: 'pointer', fontSize: 12, marginBottom: -1 });

  const renderSaveBtn = () => canEdit ? (
    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 20, borderTop: '1px solid #f0f0f0', marginTop: 16 }}>
      <button className="btn-primary" onClick={handleSave} disabled={loading} style={{ padding: '8px 32px' }}>
        {loading ? (isZh ? '儲存中...' : 'Saving...') : t('common.save')}
      </button>
    </div>
  ) : null;

  const renderRichText = (value: string, setter: (v: string) => void, ph: string) => canEdit ? (
    <RichTextEditor value={value} onChange={setter} placeholder={ph} minHeight="200px" />
  ) : (
    <div style={{ padding: 16, fontSize: 15, color: '#333', lineHeight: 1.8, minHeight: 200 }} dangerouslySetInnerHTML={{ __html: value || '-' }} />
  );

  // ── 公式 DataTable ─────────────────────────────────────────────
  const renderFormulaTable = (field: FormulaFieldKey) => {
    const formulas = getFormulas(field);
    return (
      <div>
        {canEdit && (
          <div style={{ marginBottom: 12, textAlign: 'right' }}>
            <button className="btn-primary" onClick={() => openWizard(field)}>{t('common.create')}</button>
          </div>
        )}
        {formulas.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#999', fontSize: 14 }}>
            {isZh ? '尚無公式，請點擊「新增」' : 'No formulas yet. Click "Create".'}
          </div>
        ) : (
          <div className="data-table-container">
            <table className="data-table">
              <thead className="table-header-dark-green">
                <tr>
                  <th style={{ width: '5%' }}>#</th>
                  <th style={{ width: '35%' }}>{isZh ? '公式' : 'Equation'}</th>
                  <th style={{ width: '35%' }}>{isZh ? '參數' : 'Parameters'}</th>
                  <th style={{ width: '15%' }}>{isZh ? '計算結果' : 'Result'}</th>
                  <th style={{ width: '10%' }}>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {formulas.map((f, idx) => (
                  <tr key={f.id}>
                    <td>{idx + 1}</td>
                    <td>
                      {f.equation.latex && (() => { try { return <BlockMath math={f.equation.latex} />; } catch { return <span style={{ color: '#c33' }}>LaTeX Error</span>; } })()}
                    </td>
                    <td>
                      {f.parameters.map((p, pi) => (
                        <div key={pi} style={{ fontSize: 13, marginBottom: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
                          {(() => { try { return <InlineMath math={p.symbol} />; } catch { return <span>{p.symbol}</span>; } })()}
                          {p.annual_value !== null && <span>= {fmtNum(p.annual_value)}</span>}
                          {p.unit && (() => { try { return <InlineMath math={p.unit} />; } catch { return <span>{p.unit}</span>; } })()}
                        </div>
                      ))}
                    </td>
                    <td style={{ fontWeight: 600 }}>{fmtNum(f.calculation_result?.value)}</td>
                    <td className="actions">
                      {canEdit && (
                        <>
                          <button className="btn-edit" onClick={() => openWizard(field, f)} style={{ marginRight: 8 }}>{t('common.edit')}</button>
                          <button className="btn-delete" onClick={() => deleteFormula(field, f.id)}>{t('common.delete')}</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ── 渲染 ───────────────────────────────────────────────────────
  return (
    <div className="page-container">
      <div className="page-header"><FunctionPageHeader funcCode="nsection_b10" /></div>
      {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

      {!selectedProposalId ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#999', fontSize: 16 }}>
          {isZh ? '請先在上方選擇方法論提案' : 'Please select a methodology proposal from the top bar'}
        </div>
      ) : loading && !data ? (
        <div className="loading">{t('common.loading')}</div>
      ) : (
        <div>
          {/* 主頁籤 */}
          <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: 16, flexWrap: 'wrap' }}>
            {mainTabs.map(tab => <button key={tab.key} onClick={() => setMainTab(tab.key)} style={tabBtn(mainTab === tab.key)}>{tab.label}</button>)}
          </div>

          {/* 10.1 基線方法選擇 */}
          {mainTab === '10.1' && (
            <div style={cardStyle}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
                {isZh ? '10.1 基線方法選擇' : '10.1 Selection of baseline approaches'}
              </div>
              <p style={{ fontSize: 14, color: '#555', marginBottom: 16 }}>
                {isZh ? '依基線標準第 19 段，選擇適用的基線方法：' : 'As per paragraph 19 of the baseline standard:'}
              </p>
              <div style={{ marginBottom: 20 }}>
                {APPROACH_OPTIONS.map(opt => (
                  <label key={opt.value} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 16px', marginBottom: 8,
                    background: baselineApproaches.includes(opt.value) ? '#f0f7ff' : '#fafafa',
                    border: baselineApproaches.includes(opt.value) ? '1px solid #4A90E2' : '1px solid #e8e8e8',
                    borderRadius: 8, cursor: canEdit ? 'pointer' : 'default',
                  }}>
                    <input type="checkbox" checked={baselineApproaches.includes(opt.value)}
                      onChange={() => setBaselineApproaches(prev => prev.includes(opt.value) ? prev.filter(v => v !== opt.value) : [...prev, opt.value])}
                      disabled={!canEdit} style={{ width: 18, height: 18, marginTop: 2, flexShrink: 0 }} />
                    <span style={{ fontSize: 14, lineHeight: 1.6 }}>{isZh ? opt.labelZh : opt.labelEn}</span>
                  </label>
                ))}
              </div>
              <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 14 }}>{isZh ? '選擇理由說明' : 'Justification'}</div>
              {renderRichText(approachJustification, setApproachJustification, isZh ? '請說明選擇理由...' : 'Justify the choice...')}
              {renderSaveBtn()}
            </div>
          )}

          {/* 10.2 所選方法的應用（子頁籤） */}
          {mainTab === '10.2' && (
            <div>
              <div style={{ display: 'flex', borderBottom: '1px solid #d9d9d9', marginBottom: 12 }}>
                <button onClick={() => setSubTab102('10.2.1')} style={subTabBtn(subTab102 === '10.2.1')}>
                  {isZh ? '10.2.1 基線情境識別程序' : '10.2.1 Baseline scenario identification'}
                </button>
                <button onClick={() => setSubTab102('10.2.2')} style={subTabBtn(subTab102 === '10.2.2')}>
                  {isZh ? '10.2.2 基線排放計算' : '10.2.2 Baseline emissions calculation'}
                </button>
              </div>
              <div style={cardStyle}>
                {subTab102 === '10.2.1' && (
                  <>
                    <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
                      {isZh ? '10.2.1 基線情境識別程序' : '10.2.1 Procedure for identification of baseline scenario'}
                    </div>
                    {renderRichText(baselineIdentification, setBaselineIdentification, isZh ? '請輸入...' : 'Enter...')}
                  </>
                )}
                {subTab102 === '10.2.2' && (
                  <>
                    <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
                      {isZh ? '10.2.2 向下調整前基線排放計算' : '10.2.2 Baseline emissions prior to downward adjustment'}
                    </div>
                    {renderFormulaTable('baseline_calculate')}
                  </>
                )}
                {renderSaveBtn()}
              </div>
            </div>
          )}

          {/* 10.3 */}
          {mainTab === '10.3' && (
            <div style={cardStyle}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
                {isZh ? '10.3 向下調整後基線計算' : '10.3 Calculation of the downward adjusted baseline'}
              </div>
              {renderRichText(downwardAdjustedBaseline, setDownwardAdjustedBaseline, isZh ? '請輸入...' : 'Enter...')}
              {renderSaveBtn()}
            </div>
          )}

          {/* 10.4（子頁籤） */}
          {mainTab === '10.4' && (
            <div>
              <div style={{ display: 'flex', borderBottom: '1px solid #d9d9d9', marginBottom: 12 }}>
                <button onClick={() => setSubTab104('10.4')} style={subTabBtn(subTab104 === '10.4')}>
                  {isZh ? '10.4 BAU 情境識別' : '10.4 BAU scenario identification'}
                </button>
                <button onClick={() => setSubTab104('10.4.1')} style={subTabBtn(subTab104 === '10.4.1')}>
                  {isZh ? '10.4.1 BAU 排放量計算' : '10.4.1 BAU emissions calculation'}
                </button>
              </div>
              <div style={cardStyle}>
                {subTab104 === '10.4' && (
                  <>
                    <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
                      {isZh ? '10.4 保守 BAU 情境識別' : '10.4 Identification of the conservative BAU scenario'}
                    </div>
                    {renderRichText(bauScenarioIdentification, setBauScenarioIdentification, isZh ? '請輸入...' : 'Enter...')}
                  </>
                )}
                {subTab104 === '10.4.1' && (
                  <>
                    <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
                      {isZh ? '10.4.1 保守 BAU 排放量計算' : '10.4.1 Calculation of the conservative BAU emissions'}
                    </div>
                    {renderFormulaTable('bau_calculate')}
                  </>
                )}
                {renderSaveBtn()}
              </div>
            </div>
          )}

          {/* 10.5 */}
          {mainTab === '10.5' && (() => {
            // 計算基線總和
            const baselineTotal = (baselineCalculate || []).reduce((sum, f) => sum + (f.calculation_result?.value || 0), 0);
            const baselineUnit = (baselineCalculate || []).length > 0 ? (baselineCalculate[0]?.calculation_result?.unit || '') : '';
            // 計算 BAU 總和
            const bauTotal = (bauCalculate || []).reduce((sum, f) => sum + (f.calculation_result?.value || 0), 0);
            const bauUnit = (bauCalculate || []).length > 0 ? (bauCalculate[0]?.calculation_result?.unit || '') : '';
            const diff = bauTotal - baselineTotal;
            const isValid = baselineTotal <= bauTotal;

            return (
              <div style={cardStyle}>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
                  {isZh ? '10.5 調整後基線與 BAU 比較' : '10.5 Comparison of adjusted baseline and conservative BAU'}
                </div>

                {/* 比較摘要 */}
                <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
                  {/* Baseline */}
                  <div style={{ flex: 1, minWidth: 200, padding: '16px 20px', background: '#f0f7ff', borderRadius: 8, border: '1px solid #bde0ff' }}>
                    <div style={{ fontSize: 13, color: '#4A90E2', fontWeight: 600, marginBottom: 8 }}>
                      {isZh ? '基線排放計算總和 (10.2.2)' : 'Baseline Emissions Total (10.2.2)'}
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#1a1a1a' }}>
                      {baselineCalculate.length > 0 ? fmtNum(baselineTotal) : '—'}
                      {baselineUnit && <span style={{ fontSize: 14, fontWeight: 400, marginLeft: 6, color: '#555' }}>
                        {(() => { try { return <InlineMath math={baselineUnit} />; } catch { return <span>{baselineUnit}</span>; } })()}
                      </span>}
                    </div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                      {baselineCalculate.length} {isZh ? '組公式' : 'formula(s)'}
                    </div>
                  </div>

                  {/* BAU */}
                  <div style={{ flex: 1, minWidth: 200, padding: '16px 20px', background: '#fff7e6', borderRadius: 8, border: '1px solid #ffe0b2' }}>
                    <div style={{ fontSize: 13, color: '#e67e22', fontWeight: 600, marginBottom: 8 }}>
                      {isZh ? 'BAU 排放計算總和 (10.4.1)' : 'BAU Emissions Total (10.4.1)'}
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#1a1a1a' }}>
                      {bauCalculate.length > 0 ? fmtNum(bauTotal) : '—'}
                      {bauUnit && <span style={{ fontSize: 14, fontWeight: 400, marginLeft: 6, color: '#555' }}>
                        {(() => { try { return <InlineMath math={bauUnit} />; } catch { return <span>{bauUnit}</span>; } })()}
                      </span>}
                    </div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                      {bauCalculate.length} {isZh ? '組公式' : 'formula(s)'}
                    </div>
                  </div>

                  {/* 差額 */}
                  <div style={{ flex: 1, minWidth: 200, padding: '16px 20px', background: isValid ? '#f6ffed' : '#fff2f0', borderRadius: 8, border: `1px solid ${isValid ? '#b7eb8f' : '#ffccc7'}` }}>
                    <div style={{ fontSize: 13, color: isValid ? '#52c41a' : '#e53935', fontWeight: 600, marginBottom: 8 }}>
                      {isZh ? '差額 (BAU − Baseline)' : 'Difference (BAU − Baseline)'}
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: isValid ? '#52c41a' : '#e53935' }}>
                      {(baselineCalculate.length > 0 || bauCalculate.length > 0) ? fmtNum(diff) : '—'}
                    </div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>
                      {(baselineCalculate.length > 0 || bauCalculate.length > 0) ? (
                        isValid
                          ? <span style={{ color: '#52c41a' }}>✅ Baseline ≤ BAU {isZh ? '（符合）' : '(Valid)'}</span>
                          : <span style={{ color: '#e53935' }}>❌ Baseline &gt; BAU {isZh ? '（不符合）' : '(Invalid)'}</span>
                      ) : (
                        <span style={{ color: '#999' }}>{isZh ? '請先完成 10.2.2 和 10.4.1 的計算' : 'Please complete 10.2.2 and 10.4.1 calculations first'}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* 使用者說明 */}
                <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 14 }}>
                  {isZh ? '比較說明' : 'Comparison Description'}
                </div>
                {renderRichText(baselineBauComparison, setBaselineBauComparison, isZh ? '請說明調整後基線與 BAU 的比較結果及是否需要進一步調整...' : 'Describe the comparison result and whether further adjustment is needed...')}
                {renderSaveBtn()}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── FormulaWizard Modal ────────────────────────────────── */}
      {wizardOpen && (
        <div className="modal-overlay" onClick={() => setWizardOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '92vw', width: 1200 }}>
            <div className="modal-header">
              <h2>{wizardEditEntry ? (isZh ? '編輯公式' : 'Edit Formula') : (isZh ? '新增公式' : 'Add Formula')}</h2>
              <button className="modal-close" onClick={() => setWizardOpen(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {/* 步驟列 */}
              <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid #e0e0e0', paddingBottom: 12 }}>
                {(['equations', 'parameters', 'conservative'] as WizardStage[]).map((s, i) => {
                  const labels = { equations: isZh ? '公式' : 'Equation', parameters: isZh ? '參數定義' : 'Parameters', conservative: isZh ? '保守估算' : 'Conservative' };
                  const ci = (['equations', 'parameters', 'conservative'] as WizardStage[]).indexOf(wizardStage);
                  return (
                    <div key={s} style={{ flex: 1, textAlign: 'center' }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', margin: '0 auto 4px', background: i < ci ? '#52c41a' : i === ci ? '#4A90E2' : '#e0e0e0', color: i <= ci ? '#fff' : '#999', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 13 }}>
                        {i < ci ? '✓' : i + 1}
                      </div>
                      <div style={{ fontSize: 12, color: i === ci ? '#4A90E2' : '#999', fontWeight: i === ci ? 600 : 400 }}>{labels[s]}</div>
                    </div>
                  );
                })}
              </div>

              {/* Stage 1: 公式 */}
              {wizardStage === 'equations' && (
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>{isZh ? '公式說明' : 'Equation Note'}</label>
                    <RichTextEditor value={wizardEntry.equation_note} onChange={v => setWizardEntry(prev => ({ ...prev, equation_note: v }))} placeholder={isZh ? '說明此公式用途...' : 'Describe this equation...'} minHeight="80px" />
                  </div>
                  {/* 從公式庫選取 */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>{isZh ? '從公式庫選取（選填）' : 'Select from Formula Library (optional)'}</label>
                    <select value={wizardFormulaId} onChange={e => {
                      const id = e.target.value ? Number(e.target.value) : '';
                      setWizardFormulaId(id);
                      if (!id) return;
                      const fc = wizardFormulaConfigs.find(f => f.id === id);
                      if (fc?.formula_latex) setWizardEntry(prev => ({ ...prev, equation: { latex: fc.formula_latex! } }));
                    }} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 14 }}>
                      <option value="">-- {isZh ? '手動輸入' : 'Enter manually'} --</option>
                      {wizardFormulaConfigs.map(f => <option key={f.id} value={f.id}>{f.formula_code}：{isZh ? f.name_zh : f.name_en}</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <label style={{ fontWeight: 600 }}>{isZh ? 'LaTeX 公式' : 'LaTeX Equation'}</label>
                    <LatexHelper isZh={isZh} onInsert={snippet => setWizardEntry(prev => ({ ...prev, equation: { latex: prev.equation.latex + snippet } }))} />
                  </div>
                  <textarea value={wizardEntry.equation.latex} onChange={e => setWizardEntry(prev => ({ ...prev, equation: { latex: e.target.value } }))}
                    placeholder="e.g. BE_{y} = \sum_{i} (AD_{i,y} \times EF_{i,y})"
                    style={{ width: '100%', minHeight: 80, padding: '10px 12px', fontFamily: 'monospace', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }} />
                  {wizardEntry.equation.latex.trim() && (
                    <div style={{ marginTop: 12, padding: '12px 16px', background: '#f8f8f8', borderRadius: 6, border: '1px solid #e8e8e8' }}>
                      <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>{isZh ? '預覽' : 'Preview'}</div>
                      {(() => { try { return <BlockMath math={wizardEntry.equation.latex} />; } catch { return <span style={{ color: '#c33' }}>LaTeX Error</span>; } })()}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                    <button className="btn-secondary" onClick={() => setWizardOpen(false)}>{t('common.cancel')}</button>
                    <button className="btn-primary" disabled={!wizardEntry.equation.latex.trim()} onClick={() => {
                      const parsed = parseLatexSymbols(wizardEntry.equation.latex);
                      const cfgParams = wizardFormulaId ? (wizardFormulaConfigs.find(f => f.id === wizardFormulaId)?.parameters ?? []) : [];
                      if (parsed.length > 0) {
                        setWizardEntry(prev => {
                          const existing = new Set(prev.parameters.map(p => p.symbol));
                          const newP = parsed.filter(s => !existing.has(s)).map(s => {
                            const cp = cfgParams.find(p => p.symbol === s);
                            return { symbol: s, para_type: null, scenarios: cp ? (isZh ? [cp.name_zh, cp.description_zh] : [cp.name_en || cp.name_zh, cp.description_en || cp.description_zh]).filter(Boolean).join(' ') : '', unit: cp?.unit ?? '', annual_value: null };
                          });
                          return { ...prev, parameters: [...prev.parameters, ...newP] };
                        });
                      }
                      setWizardStage('parameters');
                    }}>{isZh ? '進入參數定義 →' : 'Parameters →'}</button>
                  </div>
                </div>
              )}

              {/* Stage 2: 參數定義 */}
              {wizardStage === 'parameters' && (
                <div>
                  {/* 公式回顯 */}
                  <div style={{ padding: '10px 16px', background: '#f0f7ff', borderRadius: 6, border: '1px solid #bde0ff', marginBottom: 16 }}>
                    <div style={{ fontSize: 12, color: '#4A90E2', marginBottom: 4, fontWeight: 600 }}>{isZh ? '公式' : 'Equation'}</div>
                    {(() => { try { return <BlockMath math={wizardEntry.equation.latex} />; } catch { return null; } })()}
                  </div>

                  {/* 參數表格 */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <label style={{ fontWeight: 600 }}>{isZh ? '參數列表' : 'Parameters'}</label>
                    <button className="btn-secondary" onClick={() => setWizardEntry(prev => ({ ...prev, parameters: [...prev.parameters, { symbol: '', para_type: null, scenarios: '', unit: '', annual_value: null }] }))} style={{ fontSize: 13 }}>
                      + {isZh ? '新增參數' : 'Add Parameter'}
                    </button>
                  </div>
                  {wizardEntry.parameters.length > 0 ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
                      <thead>
                        <tr style={{ background: '#f5f5f5' }}>
                          <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12, width: '15%' }}>{isZh ? '符號' : 'Symbol'}</th>
                          <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12, width: '12%' }}>{isZh ? '參數類別' : 'Type'}</th>
                          <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12, width: '43%' }}>{isZh ? '說明' : 'Description'}</th>
                          <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12, width: '26%' }}>{isZh ? '單位' : 'Unit'}</th>
                          <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', width: '4%' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {wizardEntry.parameters.map((p, idx) => (
                          <tr key={idx}>
                            <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0' }}>
                              <input value={p.symbol} onChange={e => updateParam(idx, { symbol: e.target.value })}
                                style={{ width: '100%', border: 'none', outline: 'none', fontFamily: 'monospace', fontSize: 13 }} placeholder="AD_{i,y}" />
                              {p.symbol && (() => { try { return <div style={{ marginTop: 4 }}><InlineMath math={p.symbol} /></div>; } catch { return null; } })()}
                            </td>
                            <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0', minWidth: 140 }}>
                              <select value={p.para_type ?? ''} onChange={e => updateParam(idx, { para_type: e.target.value ? Number(e.target.value) : null })}
                                style={{ width: '100%', border: 'none', outline: 'none', fontSize: 13, background: 'transparent' }}>
                                <option value="">{isZh ? '皆適用' : 'All'}</option>
                                {paraTypeCodes.map(c => <option key={c.id} value={c.id}>{isZh ? c.code_cname : c.code_ename}</option>)}
                              </select>
                            </td>
                            <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0', minWidth: 200 }}>
                              <RichTextEditor
                                value={p.scenarios}
                                onChange={html => updateParam(idx, { scenarios: html })}
                                placeholder={isZh ? '使用情境說明...' : 'Description of usage...'}
                                minHeight="72px"
                                toolbar="inline"
                              />
                            </td>
                            <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0' }}>
                              <UnitInput value={p.unit} onChange={v => updateParam(idx, { unit: v })} />
                            </td>
                            <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0', textAlign: 'center' }}>
                              <button type="button" onClick={() => removeParam(idx)}
                                style={{ background: 'none', border: 'none', color: '#e53935', cursor: 'pointer', fontSize: 16 }}>✕</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ padding: 12, color: '#aaa', textAlign: 'center', border: '1px dashed #e0e0e0', borderRadius: 6, marginBottom: 16 }}>
                      {isZh ? '尚無參數，點擊「新增參數」或回上一步自動解析' : 'No parameters. Click "Add Parameter" or go back to auto-parse.'}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                    <button className="btn-secondary" onClick={() => setWizardStage('equations')}>← {isZh ? '回公式' : 'Back'}</button>
                    <button className="btn-primary" onClick={() => setWizardStage('conservative')}>{isZh ? '進入保守估算 →' : 'Conservative →'}</button>
                  </div>
                </div>
              )}

              {/* Stage 3: 保守估算 */}
              {wizardStage === 'conservative' && (
                <div>
                  {/* 公式回顯 */}
                  <div style={{ padding: '10px 16px', background: '#f0f7ff', borderRadius: 6, border: '1px solid #bde0ff', marginBottom: 16 }}>
                    <div style={{ fontSize: 12, color: '#4A90E2', marginBottom: 4, fontWeight: 600 }}>{isZh ? '公式' : 'Equation'}</div>
                    {(() => { try { return <BlockMath math={wizardEntry.equation.latex} />; } catch { return null; } })()}
                  </div>

                  {/* 參數數值輸入 */}
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>{isZh ? '保守估算值' : 'Conservative Estimates'}</label>
                  {wizardEntry.parameters.length > 0 ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
                      <thead>
                        <tr style={{ background: '#f5f5f5' }}>
                          <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12 }}>{isZh ? '參數' : 'Parameter'}</th>
                          <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12 }}>{isZh ? '說明' : 'Description'}</th>
                          <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12 }}>{isZh ? '單位' : 'Unit'}</th>
                          <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12 }}>{isZh ? '年度預估值' : 'Annual Value'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wizardEntry.parameters.map((p, idx) => (
                          <tr key={idx}>
                            <td style={{ padding: '4px 10px', border: '1px solid #e0e0e0', fontFamily: 'monospace' }}>
                              {(() => { try { return <InlineMath math={p.symbol} />; } catch { return <span>{p.symbol}</span>; } })()}
                            </td>
                            <td style={{ padding: '4px 10px', border: '1px solid #e0e0e0', fontSize: 13, color: '#666' }} dangerouslySetInnerHTML={{ __html: p.scenarios || '—' }} />
                            <td style={{ padding: '4px 10px', border: '1px solid #e0e0e0' }}>
                              {p.unit ? (() => { try { return <InlineMath math={p.unit} />; } catch { return <span>{p.unit}</span>; } })() : '—'}
                            </td>
                            <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0' }}>
                              <input type="number" step="0.01" value={p.annual_value ?? ''}
                                onChange={e => updateParam(idx, { annual_value: e.target.value ? Number(e.target.value) : null })}
                                style={{ width: '100%', border: 'none', outline: 'none', fontSize: 13, textAlign: 'right' }} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ padding: 12, color: '#aaa', textAlign: 'center', border: '1px dashed #e0e0e0', borderRadius: 6, marginBottom: 16 }}>
                      {isZh ? '尚無參數' : 'No parameters'}
                    </div>
                  )}

                  {/* 計算區 */}
                  <div style={{ marginTop: 16, padding: '14px 16px', background: '#f8faff', borderRadius: 8, border: '1px solid #d0e4ff' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      {/* 結果單位 */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <label style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>{isZh ? '結果單位' : 'Result Unit'}</label>
                        <div style={{ width: 220 }}>
                          <UnitInput
                            value={wizardEntry.calculation_result.unit}
                            onChange={v => setWizardEntry(prev => ({ ...prev, calculation_result: { ...prev.calculation_result, unit: v } }))}
                          />
                        </div>
                      </div>
                      {/* 計算按鈕 */}
                      <button onClick={() => {
                        const val = evaluateLatex(wizardEntry.equation.latex, wizardEntry.parameters);
                        if (val !== null) {
                          setWizardEntry(prev => ({ ...prev, calculation_result: { ...prev.calculation_result, value: val } }));
                        } else {
                          toast.warning(isZh ? '無法自動計算，請確認所有參數皆已填入數值' : 'Cannot calculate. Please ensure all parameter values are filled.');
                        }
                      }} style={{ padding: '7px 20px', background: '#52c41a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                        ▶ {isZh ? '計算' : 'Calculate'}
                      </button>
                      {/* 計算結果 */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                        <span style={{ fontSize: 13, color: '#888' }}>{isZh ? '計算結果' : 'Result'}</span>
                        <span style={{ fontSize: 20, fontWeight: 700, color: wizardEntry.calculation_result.value !== null ? '#1a1a1a' : '#ccc', minWidth: 80, textAlign: 'right' }}>
                          {fmtNum(wizardEntry.calculation_result.value)}
                        </span>
                        {wizardEntry.calculation_result.unit && (
                          <span style={{ fontSize: 13, color: '#555' }}>
                            {(() => { try { return <InlineMath math={wizardEntry.calculation_result.unit} />; } catch { return <span>{wizardEntry.calculation_result.unit}</span>; } })()}
                          </span>
                        )}
                      </div>
                    </div>
                    {wizardEntry.calculation_result.value !== null && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#52c41a' }}>✓ {isZh ? '計算完成' : 'Calculation complete'}</div>
                    )}
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                    <button className="btn-secondary" onClick={() => setWizardStage('parameters')}>← {isZh ? '回參數定義' : 'Back'}</button>
                    <button className="btn-primary" onClick={handleWizardSave}>{isZh ? '儲存公式' : 'Save Formula'}</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NsectionB10Page;
